import * as THREE from 'three';
import { CELL_SIZE } from '../game/config';
import { cellToWorld } from '../game/NavigationSystem';
import type { LocationDefinition, Obstacle, SceneLights } from '../game/types';
import { optionalElement } from '../core/dom';
import { locationForLevel, MAPS } from './assets';
import { makeEnvironmentMaterial } from './materials';

interface EnvironmentDependencies {
  scene: THREE.Scene;
  ground: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshStandardMaterial>;
  outerGround: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshStandardMaterial>;
  grid: THREE.GridHelper;
  group: THREE.Group;
  lights: SceneLights;
  obstacles: Obstacle[];
}

export class EnvironmentSystem {
  private active: LocationDefinition = locationForLevel(1);

  constructor(private readonly deps: EnvironmentDependencies) {}

  get current(): LocationDefinition { return this.active; }

  apply(level: number): LocationDefinition {
    const location = locationForLevel(level);
    this.active = location;
    const { scene, ground, outerGround, grid, lights } = this.deps;
    ground.material.map = location.groundMap;
    ground.material.emissiveMap = location.groundMap;
    ground.material.color.setHex(location.fieldColor);
    ground.material.emissive.setHex(location.fieldEmissive);
    ground.material.needsUpdate = true;

    const backdropMap = location.groundMap.clone();
    backdropMap.wrapS = backdropMap.wrapT = THREE.RepeatWrapping;
    backdropMap.repeat.set(20, 20);
    backdropMap.colorSpace = THREE.SRGBColorSpace;
    const oldMap = outerGround.userData.locationMap as THREE.Texture | undefined;
    oldMap?.dispose();
    outerGround.userData.locationMap = backdropMap;
    outerGround.material.map = backdropMap;
    outerGround.material.color.setHex(location.fieldColor);
    outerGround.material.needsUpdate = true;

    (scene.background as THREE.Color).setHex(location.background);
    lights.hemisphere.color.setHex(location.hemisphereSky);
    lights.hemisphere.groundColor.setHex(location.hemisphereGround);
    lights.ambient.color.setHex(location.ambient);
    lights.key.color.setHex(location.keyLight);
    lights.fill.color.setHex(location.fillLight);
    const gridMaterial = grid.material as THREE.LineBasicMaterial;
    gridMaterial.vertexColors = false;
    gridMaterial.color.setHex(location.gridMajor);
    gridMaterial.needsUpdate = true;
    this.updateObstacleTheme(location);
    this.clearDecor();
    this.createRiversAndBridges(location);
    if (location.decor === 'desert') this.createDesertDecor();
    if (location.decor === 'snow') this.createSnowDecor();
    if (location.decor === 'canyon') this.createCanyonDecor();
    const label = optionalElement<HTMLElement>('#locationValue');
    if (label) label.textContent = location.name;
    return location;
  }

  private updateObstacleTheme(location: LocationDefinition): void {
    this.deps.obstacles.forEach((obstacle) => {
      obstacle.mesh.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        const material = object.material;
        if (Array.isArray(material) || !(material instanceof THREE.MeshStandardMaterial) || !material.map) return;
        material.map = MAPS.brick;
        material.color.setHex(obstacle.type === 'brick' ? location.brickColor : location.rockColor);
        material.emissive.setHex(obstacle.type === 'brick' ? 0x351b14 : 0x242b2e);
        material.needsUpdate = true;
      });
    });
  }

  private clearDecor(): void {
    const group = this.deps.group;
    while (group.children.length) {
      const child = group.children[0];
      group.remove(child);
      child.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => material.dispose());
        }
      });
    }
  }

  private addObject(object: THREE.Object3D, x: number, z: number, scale = 1): THREE.Object3D {
    object.position.set(x, 0, z);
    object.scale.setScalar(scale);
    this.deps.group.add(object);
    return object;
  }

  private createRiversAndBridges(location: LocationDefinition): void {
    const riverColor = location.key === 'snow' ? 0x62bfd7 : location.key === 'canyon' ? 0x216d84 : 0x27849a;
    const riverEdgeColor = location.key === 'snow' ? 0xb5f1f2 : 0x6fd3d1;
    const bridgeColor = location.key === 'snow' ? 0x8b7058 : location.key === 'canyon' ? 0x725038 : 0x8d5d3c;
    const bridgeHighlight = location.key === 'snow' ? 0xc7b39a : 0xc07b48;
    const bridgeKeys = new Set(location.bridgeCells.map((cell) => `${cell.col}:${cell.row}`));

    location.riverCells.forEach((cell, index) => {
      const point = cellToWorld(cell.col, cell.row);
      const water = new THREE.Mesh(
        new THREE.PlaneGeometry(CELL_SIZE * 0.98, CELL_SIZE * 0.98),
        makeEnvironmentMaterial(riverColor, { roughness: 0.28, metalness: 0.2, emissive: 0x082f3d, emissiveIntensity: 0.34 }),
      );
      water.rotation.x = -Math.PI / 2;
      water.position.set(point.x, 0.035, point.z);
      water.receiveShadow = true;
      water.renderOrder = 1;
      this.deps.group.add(water);

      if (index % 2 === 0 && !bridgeKeys.has(`${cell.col}:${cell.row}`)) {
        const ripple = new THREE.Mesh(
          new THREE.PlaneGeometry(CELL_SIZE * 0.5, 0.035),
          new THREE.MeshBasicMaterial({ color: riverEdgeColor, transparent: true, opacity: 0.36 }),
        );
        ripple.rotation.x = -Math.PI / 2;
        ripple.rotation.z = (cell.col + cell.row) % 2 ? 0.18 : -0.18;
        ripple.position.set(point.x + (cell.col % 2 ? 0.2 : -0.18), 0.052, point.z + (cell.row % 2 ? 0.28 : -0.22));
        ripple.renderOrder = 2;
        this.deps.group.add(ripple);
      }
    });

    location.bridgeCells.forEach((cell) => {
      const point = cellToWorld(cell.col, cell.row);
      const bridge = new THREE.Group();
      const deck = new THREE.Mesh(
        new THREE.BoxGeometry(CELL_SIZE * 0.96, 0.16, CELL_SIZE * 0.96),
        makeEnvironmentMaterial(bridgeColor, { roughness: 0.88, metalness: 0.05 }),
      );
      deck.position.y = 0.13;
      deck.castShadow = true;
      deck.receiveShadow = true;
      bridge.add(deck);

      const horizontalRiver = location.riverCells.some((riverCell) => riverCell.row === cell.row && riverCell.col !== cell.col);
      for (let plankIndex = 0; plankIndex < 5; plankIndex++) {
        const plank = new THREE.Mesh(
          horizontalRiver
            ? new THREE.BoxGeometry(CELL_SIZE * 0.86, 0.045, 0.16)
            : new THREE.BoxGeometry(0.16, 0.045, CELL_SIZE * 0.86),
          makeEnvironmentMaterial(bridgeHighlight, { roughness: 0.84, metalness: 0.03 }),
        );
        if (horizontalRiver) plank.position.z = -0.72 + plankIndex * 0.36;
        else plank.position.x = -0.72 + plankIndex * 0.36;
        plank.position.y = 0.235;
        plank.castShadow = true;
        bridge.add(plank);
      }

      bridge.position.set(point.x, 0, point.z);
      this.deps.group.add(bridge);
    });
  }

  private addDune(x: number, z: number, width: number, depth: number, color = 0xc08b54): void {
    const dune = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 10), makeEnvironmentMaterial(color, { roughness: 1 }));
    dune.scale.set(width, 0.42, depth);
    dune.position.set(x, 0.12, z);
    dune.castShadow = true;
    dune.receiveShadow = true;
    this.deps.group.add(dune);
  }

  private addPalm(x: number, z: number, scale = 1, lean = 0): void {
    const tree = new THREE.Group();
    const trunkMaterial = makeEnvironmentMaterial(0x80502e, { roughness: 1 });
    const leafMaterial = makeEnvironmentMaterial(0x39734f, { roughness: 0.86, emissive: 0x102c1d, emissiveIntensity: 0.18 });
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.28, 3.1, 8), trunkMaterial);
    trunk.position.y = 1.55;
    trunk.rotation.z = lean;
    trunk.castShadow = true;
    tree.add(trunk);
    for (let i = 0; i < 9; i++) {
      const angle = i / 9 * Math.PI * 2;
      const leaf = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.07, 1.45), leafMaterial);
      leaf.position.set(Math.cos(angle) * 0.58, 3.08, Math.sin(angle) * 0.58);
      leaf.rotation.y = Math.PI / 2 - angle;
      leaf.rotation.x = 0.25;
      leaf.castShadow = true;
      tree.add(leaf);
    }
    for (let i = 0; i < 3; i++) {
      const fruit = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 6), makeEnvironmentMaterial(0x4f2c1d));
      fruit.position.set(Math.cos(i * 2.1) * 0.22, 2.84, Math.sin(i * 2.1) * 0.22);
      tree.add(fruit);
    }
    this.addObject(tree, x, z, scale);
  }

  private addCactus(x: number, z: number, scale = 1): void {
    const cactus = new THREE.Group();
    const material = makeEnvironmentMaterial(0x4e7652, { roughness: 0.92, emissive: 0x13271b, emissiveIntensity: 0.16 });
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.3, 2.1, 8), material);
    body.position.y = 1.05;
    body.castShadow = true;
    cactus.add(body);
    const top = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), material);
    top.scale.y = 0.65;
    top.position.y = 2.08;
    cactus.add(top);
    const arms: Array<readonly [number, number, number]> = [[-0.42, 1.25, -0.45], [0.42, 1.55, 0.4]];
    arms.forEach(([xOffset, y, rotation]) => {
      const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.17, 0.9, 8), material);
      arm.position.set(xOffset, y, 0);
      arm.rotation.z = rotation;
      arm.castShadow = true;
      cactus.add(arm);
      const hand = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 0.5, 8), material);
      hand.position.set(xOffset + (xOffset < 0 ? -0.26 : 0.26), y + 0.32, 0);
      hand.rotation.z = rotation * 0.3;
      cactus.add(hand);
    });
    this.addObject(cactus, x, z, scale);
  }

  private addSnowDrift(x: number, z: number, width: number, depth: number): void {
    const drift = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 10), makeEnvironmentMaterial(0xf0f8fb, { roughness: 1, emissive: 0x63808b, emissiveIntensity: 0.06 }));
    drift.scale.set(width, 0.3, depth);
    drift.position.set(x, 0.08, z);
    drift.receiveShadow = true;
    this.deps.group.add(drift);
  }

  private addSnowman(x: number, z: number, scale = 1): void {
    const snowman = new THREE.Group();
    const snow = makeEnvironmentMaterial(0xf1f8f8, { roughness: 0.96, emissive: 0x667e86, emissiveIntensity: 0.1 });
    const coal = makeEnvironmentMaterial(0x182a31, { roughness: 0.8, emissive: 0x020405, emissiveIntensity: 0.25 });
    const orange = makeEnvironmentMaterial(0xe57b39, { roughness: 0.8, emissive: 0x4c170b, emissiveIntensity: 0.2 });
    const sections: Array<readonly [number, number]> = [[0.62, 0.62], [0.48, 1.42], [0.34, 2.08]];
    sections.forEach(([radius, y]) => {
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(radius, 16, 12), snow);
      sphere.position.y = y;
      sphere.castShadow = true;
      snowman.add(sphere);
    });
    [-0.12, 0.12].forEach((eyeX) => {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 6), coal);
      eye.position.set(eyeX, 2.17, 0.3);
      snowman.add(eye);
    });
    [-0.14, 0, 0.14].forEach((buttonX) => {
      const button = new THREE.Mesh(new THREE.SphereGeometry(0.065, 8, 6), coal);
      button.position.set(buttonX, 1.42 - Math.abs(buttonX) * 0.2, 0.45);
      snowman.add(button);
    });
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.42, 7), orange);
    nose.rotation.x = Math.PI / 2;
    nose.position.set(0, 2.05, 0.38);
    snowman.add(nose);
    const hat = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.38, 0.18, 12), coal);
    hat.position.y = 2.39;
    snowman.add(hat);
    const hatTop = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.38, 12), coal);
    hatTop.position.y = 2.64;
    snowman.add(hatTop);
    [-1, 1].forEach((side) => {
      const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.045, 0.9, 6), makeEnvironmentMaterial(0x5a3828));
      arm.position.set(side * 0.48, 1.52, 0);
      arm.rotation.z = side * 0.95;
      arm.castShadow = true;
      snowman.add(arm);
    });
    this.addObject(snowman, x, z, scale);
  }

  private addPine(x: number, z: number, scale = 1): void {
    const pine = new THREE.Group();
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.2, 1.5, 8), makeEnvironmentMaterial(0x4c392e));
    trunk.position.y = 0.75;
    pine.add(trunk);
    const green = makeEnvironmentMaterial(0x214d52, { roughness: 0.95, emissive: 0x081916, emissiveIntensity: 0.18 });
    const snow = makeEnvironmentMaterial(0xe6f3f4, { roughness: 1, emissive: 0x4a6870, emissiveIntensity: 0.08 });
    const layers: Array<readonly [number, number]> = [[0.9, 1.25], [0.7, 1.9], [0.48, 2.5]];
    layers.forEach(([radius, y], index) => {
      const layer = new THREE.Mesh(new THREE.ConeGeometry(radius, 1.2, 8), green);
      layer.position.y = y;
      layer.castShadow = true;
      pine.add(layer);
      if (index > 0) {
        const cap = new THREE.Mesh(new THREE.ConeGeometry(radius * 0.7, 0.18, 8), snow);
        cap.position.y = y + 0.42;
        pine.add(cap);
      }
    });
    this.addObject(pine, x, z, scale);
  }

  private addCanyonWall(x: number, z: number, scale = 1): void {
    const wall = new THREE.Group();
    const blocks: Array<readonly [number, number, number, number, number, number]> = [
      [1.75, 0.72, 1.15, -0.12, 0.36, 0],
      [1.38, 0.68, 1.05, 0.18, 1.06, 0.04],
      [1.82, 0.18, 1.2, -0.04, 1.49, 0],
    ];
    blocks.forEach(([width, height, depth, offsetX, y, offsetZ], index) => {
      const block = new THREE.Mesh(
        new THREE.BoxGeometry(width, height, depth),
        makeEnvironmentMaterial(index === 2 ? 0xb06346 : 0x984d35, { map: MAPS.canyon, roughness: 1, emissive: 0x35170f, emissiveIntensity: 0.12 }),
      );
      block.position.set(offsetX, y, offsetZ);
      block.rotation.y = index === 1 ? -0.05 : 0;
      block.castShadow = true;
      block.receiveShadow = true;
      wall.add(block);
    });
    this.addObject(wall, x, z, scale);
  }

  private createDesertDecor(): void {
    const dunes: Array<readonly [number, number, number, number]> = [[-20, -18, 3.4, 1.4], [19, -16, 2.8, 1.2], [-19, 17, 3.2, 1.3], [20, 13, 2.5, 1.15]];
    dunes.forEach(([x, z, width, depth]) => this.addDune(x, z, width, depth));
    this.addPalm(-19, -12, 1.15, -0.08); this.addPalm(19, -13, 0.95, 0.1); this.addPalm(-20, 14, 0.9, 0.08); this.addPalm(20, 18, 1.1, -0.05);
    this.addCactus(-17, -2, 0.9); this.addCactus(18, 5, 0.82); this.addCactus(19, -6, 0.62);
  }

  private createSnowDecor(): void {
    const drifts: Array<readonly [number, number, number, number]> = [[-20, -18, 3.6, 1.5], [19, -16, 3, 1.2], [-19, 17, 3.2, 1.4], [20, 13, 2.8, 1.3]];
    drifts.forEach(([x, z, width, depth]) => this.addSnowDrift(x, z, width, depth));
    this.addSnowman(-19, -12, 1); this.addSnowman(19, -13, 0.85); this.addSnowman(20, 16, 0.9);
    this.addPine(-20, -4, 1.05); this.addPine(19, 4, 0.9); this.addPine(-19, 15, 0.8);
  }

  private createCanyonDecor(): void {
    const stacks: Array<readonly [number, number, number]> = [[-20, -17, 1.6], [20, -15, 1.35], [-20, -6, 1.15], [20, -4, 1.65], [-20, 8, 1.5], [20, 10, 1.25], [-19, 18, 1.6], [19, 18, 1.45]];
    stacks.forEach(([x, z, scale]) => this.addCanyonWall(x, z, scale));
    this.addCactus(-17, -12, 0.82); this.addCactus(18, -1, 0.9); this.addCactus(-18, 11, 0.68);
  }
}
